<template>
  <div>
    <v-card class="d-flex justify-space-between align-center mt-4 pa-5 border" outlined>
      <h3 class="font-weight-bold">Kế hoạch giảng dạy</h3>
      <div class="d-flex align-center gap-2">
        <!-- Desktop actions -->
        <div class="d-flex" v-if="$vuetify.breakpoint.mdAndUp">
          <v-menu offset-y v-if="isEditMode">
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="warning" v-bind="attrs" v-on="on" outlined>
                <v-icon left>mdi-file-excel</v-icon> NHẬP FILE EXCEL
                <v-icon right>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="dowloadExampleFile">
                <v-list-item-content>
                  <v-list-item-title style="color: #255cc2">Download file mẫu</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item @click="uploadExcelFile">
                <v-list-item-content>
                  <v-list-item-title style="color: #255cc2">Upload file excel</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-btn class="ml-4" v-if="isEditMode" outlined color="primary" @click="openCopyTeachingPlan()">
            <v-icon left>mdi-content-copy</v-icon> SAO CHÉP KHGD
          </v-btn>

          <v-btn class="ml-4" v-if="!isEditMode" outlined color="success" @click="exportExcel()">
            <v-icon left>mdi-file-excel</v-icon> XUẤT FILE EXCEL
          </v-btn>

          <v-btn class="ml-4" v-if="!isEditMode && isUpdateMode" color="primary" @click="enableEditMode">
            CHỈNH SỬA
          </v-btn>
          <v-btn class="ml-4" v-if="isEditMode && isUpdateMode" color="error" outlined @click="cancelEditMode">
            HUỶ
          </v-btn>
        </div>

        <!-- Mobile overflow menu -->
        <v-menu offset-y v-if="!$vuetify.breakpoint.mdAndUp">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <template v-if="isEditMode">
              <v-list-item @click="dowloadExampleFile">
                <v-list-item-title>Download file mẫu</v-list-item-title>
              </v-list-item>
              <v-list-item @click="uploadExcelFile">
                <v-list-item-title>Upload file Excel</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openCopyTeachingPlan">
                <v-list-item-title>Sao chép KHGD</v-list-item-title>
              </v-list-item>
            </template>
            <template v-else>
              <v-list-item @click="exportExcel">
                <v-list-item-title>Xuất file Excel</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="isUpdateMode && !isEditMode" @click="enableEditMode">
                <v-list-item-title>Chỉnh sửa</v-list-item-title>
              </v-list-item>
            </template>
            <v-list-item v-if="isEditMode && isUpdateMode" @click="cancelEditMode">
              <v-list-item-title>Huỷ</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </v-card>

    <v-card class="pa-5 border" outlined>
      <div class="d-flex justify-end mb-4">
        <!-- Desktop: hai nút riêng biệt -->
        <div v-if="$vuetify.breakpoint.mdAndUp" class="d-flex">
          <v-btn @click="openConfirm('draft')" v-if="isEditMode" color="primary" outlined class="mr-2">
            LƯU NHÁP
          </v-btn>
          <v-btn @click="openConfirm('official')" v-if="isEditMode" color="primary">
            LƯU VÀ CÔNG KHAI CHÍNH THỨC
          </v-btn>
        </div>

        <!-- Mobile: gộp menu -->
        <div v-else>
          <v-menu offset-y>
            <template #activator="{ on, attrs }">
              <v-btn icon color="primary" v-bind="attrs" v-on="on">
                <v-icon>mdi-content-save</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="openConfirm('draft')" v-if="isEditMode">
                <v-list-item-title>Lưu nháp</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openConfirm('official')" v-if="isEditMode">
                <v-list-item-title>Lưu & Công khai</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <div class="my-8 d-flex">
        <div class="vertical-line-fixed"></div>
        <div class="flex-grow-1 content-area" style="margin-top: -16px; margin-bottom: -16px">
          <div v-for="(topic, topicIndex) in topics" :key="topic.tempId || topic.id" class="mb-8">
            <div class="d-flex align-center">
              <v-btn class="mr-2 pa-2 rounded-circle" min-width="8px" height="8px" color="primary" depressed />
              <h4 class="mb-0 mr-4" style="color: #0d47a1 !important">Chủ đề {{ topicIndex + 1 }}</h4>
              <template v-if="isEditMode">
                <v-text-field
                  v-model="topic.name"
                  label="Nhập tên chủ đề"
                  flat
                  outlined
                  dense
                  clearable
                  hide-details
                  class="mr-2"
                  style="max-width: 300px"
                />
              </template>
              <template v-else>
                <span>{{ topic.name }}</span>
              </template>
              <v-btn v-if="isEditMode" icon small @click="removeTopic(topicIndex)">
                <v-icon color="red">mdi-delete-outline</v-icon>
              </v-btn>
            </div>

            <div
              v-for="(lesson, lessonIndex) in topic.lessons"
              :key="lesson.tempId || lesson.id"
              :class="[
                'ml-12',
                'pl-12',
                'dotted-line-info',
                'pt-5',
                isEditMode ? 'is-edit' : '',
                lessonIndex === topic.lessons.length - 1 && !isEditMode ? 'last-item' : ''
              ]"
            >
              <v-row>
                <v-col cols="12" md="5" class="d-flex align-center pb-0" v-if="isEditMode">
                  <strong class="mr-0">Tiết {{ computeGlobalLessonIndex(topicIndex, lessonIndex) }}</strong>
                  <template v-if="isEditMode">
                    <span class="mx-2">-</span>
                    <v-text-field v-model="lesson.name" label="Nội dung" flat outlined dense clearable hide-details />
                  </template>
                  <template v-else>
                    <span>{{ lesson.name }}</span>
                  </template>
                </v-col>
                <v-col cols="12" md="5" class="align-center pb-0" v-else>
                  <div>
                    <strong class="mr-2">Tiết {{ computeGlobalLessonIndex(topicIndex, lessonIndex) }}</strong>
                    <template>
                      <span>{{ lesson.name }}</span>
                    </template>
                  </div>
                  <div class="d-flex flex-wrap mt-2" style="color: #797979">
                    <v-tooltip top content-class="custom-tooltip">
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">{{ lesson.activityType | teachingPlanActivityTypes }}</span>
                      </template>
                      <span>Loại hình</span>
                    </v-tooltip>
                    <span class="mx-1">/</span>
                    <v-tooltip top content-class="custom-tooltip">
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">{{ lesson.location | teachingPlanLocations }}</span>
                      </template>
                      <span>Địa điểm</span>
                    </v-tooltip>
                    <span class="mx-1">/</span>
                    <v-tooltip top content-class="custom-tooltip">
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">{{ lesson.week }}</span>
                      </template>
                      <span>Thời điểm dự kiến</span>
                    </v-tooltip>
                  </div>
                </v-col>
                <v-col cols="12" md="2" class="d-flex align-center pl-0 pb-0">
                  <template v-if="isEditMode">
                    <span class="mr-2">-</span>
                    <v-select
                      v-model="lesson.activityType"
                      :items="teachingPlanActivityTypes"
                      flat
                      outlined
                      dense
                      clearable
                      hide-details
                      label="Loại hình"
                      item-text="title"
                      item-value="status"
                    ></v-select>
                  </template>
                </v-col>
                <v-col cols="12" md="2" class="d-flex align-center pl-0 pb-0">
                  <template v-if="isEditMode">
                    <span class="mr-2">-</span>
                    <v-select
                      v-model="lesson.location"
                      :items="teachingPlanLocations"
                      flat
                      outlined
                      dense
                      clearable
                      hide-details
                      label="Địa điểm"
                      item-text="title"
                      item-value="status"
                    ></v-select>
                  </template>
                </v-col>
                <v-col cols="12" md="2" class="d-flex align-center pl-0 pb-0">
                  <template v-if="isEditMode">
                    <span class="mr-2">-</span>
                    <v-select
                      v-model="lesson.week"
                      :items="weeks"
                      label="Thời điểm dự kiến"
                      flat
                      outlined
                      dense
                      clearable
                      hide-details
                    />
                  </template>
                </v-col>
                <v-col cols="12" md="1" class="d-flex align-center pl-0 pb-0">
                  <v-btn v-if="isEditMode" icon @click="removeLesson(topicIndex, lessonIndex)">
                    <v-icon color="grey">mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </div>

            <div v-if="isEditMode" :class="['ml-12', 'pl-12', 'dotted-line', 'pt-5', isEditMode ? 'last-item' : '']">
              <v-btn outlined color="primary" @click="addLesson(topicIndex)">
                <v-icon left color="primary">mdi-plus</v-icon> THÊM TIẾT
              </v-btn>
            </div>
          </div>

          <div v-if="isEditMode" class="d-flex align-center mt-5">
            <v-btn class="mr-2 pa-2 rounded-circle" min-width="8px" height="8px" color="primary" depressed />
            <div class="text--secondary">
              <v-btn color="primary" @click="addTopic">
                <v-icon left color="white">mdi-plus</v-icon> THÊM CHỦ ĐỀ
              </v-btn>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-end my-4">
        <v-btn @click="openConfirm('draft')" v-if="isEditMode" color="primary" outlined> LƯU NHÁP </v-btn>
        <v-btn @click="openConfirm('official')" v-if="isEditMode" color="primary" class="ml-4">
          LƯU VÀ CÔNG KHAI CHÍNH THỨC
        </v-btn>
      </div>
    </v-card>
    <v-dialog v-model="confirmDialog" persistent max-width="620px">
      <v-card>
        <v-card-title class="blue darken-4 white--text">
          <v-toolbar-title>XÁC NHẬN THAO TÁC</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn dark icon @click="closeConfirm">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-6">
          <p class="ma-0">
            Thao tác này sẽ cập nhật lại trạng thái và thông tin của kế hoạch giảng dạy.<br />
            Bạn có chắc là bạn muốn thực hiện thao tác không?
          </p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="d-flex justify-space-between pa-6">
          <v-btn color="blue darken-4" text @click="closeConfirm" outlined>Hủy</v-btn>
          <v-spacer></v-spacer>
          <v-btn :loading="loadingConfirm" depressed class="ma-2" color="primary" medium @click="confirm">
            Xác nhận
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="copyDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="blue darken-4 white--text">
          <v-toolbar-title>SAO CHÉP TỪ KHGD CÓ SẴN</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn dark icon @click="copyDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-6">
          <b class="ma-0">Kế hoạch giảng dạy có sẵn</b>
          <v-select
            v-model="selectedPlanId"
            :items="teachingPlanOptions"
            :loading="loadingTeachingPlans"
            label="Chọn KHGD"
            item-text="label"
            item-value="_id"
            outlined
            dense
            clearable
          ></v-select>
        </v-card-text>
        <v-divider />
        <v-card-actions class="d-flex justify-space-between pa-6">
          <v-btn color="blue darken-1" text @click="copyDialog = false" outlined>HUỶ</v-btn>
          <v-btn color="blue darken-4" dark class="white--text elevation-0" @click="copySelectedPlan">THÊM</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import XLSX from 'xlsx'

export default {
  computed: {
    ...mapGetters('TeachingPlan', ['item']),
    ...mapState('constant', ['teachingPlanActivityTypes', 'teachingPlanLocations'])
  },
  data() {
    return {
      topics: [],
      weeks: Array.from({ length: 42 }, (_, i) => `Tuần ${i + 1}`),
      isEditMode: false,
      isUpdateMode: false,
      confirmDialog: false,
      pendingStatus: null,
      loadingConfirm: false,

      copyDialog: false,
      selectedPlanId: null,
      teachingPlanOptions: [],
      loadingTeachingPlans: false
    }
  },
  watch: {
    item: {
      handler(newVal) {
        if (newVal) {
          this.isUpdateMode = !!newVal.plan
          this.isEditMode = false

          if (newVal.plan && newVal.topics.length > 0) {
            this.isEditMode = false
            // UPDATE có sẵn dữ liệu
            this.loadExistingData(newVal.topics)
          } else if (newVal.plan && newVal.topics.length === 0) {
            // UPDATE nhưng chưa có topic nào
            this.topics = []
            this.isEditMode = false
          } else {
            // CREATE mode: luôn tạo 1 chủ đề và tiết
            this.isEditMode = true
            this.topics = []
            this.addTopic()
          }
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions('TeachingPlan', [
      'createTeachingPlan',
      'updateTeachingPlan',
      'fetchOne',
      'fetchOneForCopy',
      'getTeachingPlanForCopy'
    ]),

    openCopyTeachingPlan() {
      this.copyDialog = true
      this.selectedPlanId = null
      this.loadOfficialTeachingPlans()
    },

    async loadOfficialTeachingPlans() {
      this.loadingTeachingPlans = true
      try {
        const query = { status: 'official', _limit: -1 }
        const plans = await this.getTeachingPlanForCopy(query)
        this.teachingPlanOptions = plans.map(plan => ({
          _id: plan._id,
          label: `${plan.grade.title || ''} - ${plan.class.title || ''} - ${plan.subjectGroup.title || ''}`,
          topics: plan.topics || []
        }))
      } catch (e) {
        this.$alert.error('Không thể tải danh sách KHGD')
      } finally {
        this.loadingTeachingPlans = false
      }
    },

    async copySelectedPlan() {
      const selected = this.teachingPlanOptions.find(p => p._id === this.selectedPlanId)
      if (!selected) return
      const selectedCopy = await this.fetchOneForCopy(selected._id)
      if (!selectedCopy || !selectedCopy.topics) return
      this.topics = selectedCopy.topics.map(topic => ({
        tempId: uuidv4(),
        name: topic.name,
        lessons: (topic.lessons || []).map(lesson => ({
          tempId: uuidv4(),
          name: lesson.name,
          activityType: lesson.activityType,
          location: lesson.location,
          week: lesson.week ? `Tuần ${lesson.week}` : null
        }))
      }))
      this.copyDialog = false
      this.isEditMode = true
      this.$alert.success('Sao chép kế hoạch giảng dạy thành công')
    },
    enableEditMode() {
      this.isEditMode = true
    },
    cancelEditMode() {
      this.isEditMode = false
      if (this.item && this.item.topics) {
        this.loadExistingData(this.item.topics)
      }
    },
    loadExistingData(topics) {
      this.topics = topics.map(topic => ({
        id: topic.id,
        name: topic.name,
        lessons: topic.lessons.map(lesson => ({
          id: lesson.id,
          name: lesson.name,
          activityType: lesson.activityType,
          week: lesson.week ? `Tuần ${lesson.week}` : null,
          location: lesson.location
        }))
      }))
    },
    computeGlobalLessonIndex(topicIndex, lessonIndex) {
      let count = 0
      for (let i = 0; i < topicIndex; i++) {
        count += this.topics[i].lessons.length
      }
      return count + lessonIndex + 1
    },
    addTopic() {
      this.topics.push({
        tempId: uuidv4(),
        name: '',
        lessons: [this.createEmptyLesson()]
      })
    },
    removeTopic(index) {
      this.topics.splice(index, 1)
    },
    addLesson(topicIndex) {
      this.topics[topicIndex].lessons.push(this.createEmptyLesson())
    },
    removeLesson(topicIndex, lessonIndex) {
      this.topics[topicIndex].lessons.splice(lessonIndex, 1)
    },
    createEmptyLesson() {
      return {
        tempId: uuidv4(),
        name: '',
        activityType: '',
        week: null,
        location: ''
      }
    },
    openConfirm(status) {
      this.pendingStatus = status
      this.confirmDialog = true
    },
    closeConfirm() {
      this.confirmDialog = false
      this.pendingStatus = null
    },
    async confirm() {
      if (this.pendingStatus) {
        this.loadingConfirm = true
        try {
          await this.savePlan(this.pendingStatus)
          this.confirmDialog = false
          this.pendingStatus = null
        } catch (e) {
          this.$alert.error('Lưu kế hoạch thất bại. Vui lòng thử lại.')
        } finally {
          this.loadingConfirm = false
        }
      }
    },
    async savePlan(status) {
      const planPayload = {
        status,
        ...(this.item.teaching.class && { class: this.item.teaching.class._id }),
        ...(this.item.teaching.grade && { grade: this.item.teaching.grade._id }),
        ...(this.item.teaching.teacher && { teacher: this.item.teaching.teacher._id }),
        ...(this.item.teaching.subject && { subject: this.item.teaching.subject._id }),
        topics: this.topics.map(topic => ({
          name: topic.name,
          lessons: topic.lessons.map(lesson => ({
            name: lesson.name,
            activityType: lesson.activityType,
            week: lesson.week ? parseInt(lesson.week.replace('Tuần ', '')) : null,
            location: lesson.location
          }))
        }))
      }

      if (this.item.plan) {
        await this.updateTeachingPlan({ id: this.item.plan._id, ...planPayload })
      } else {
        await this.createTeachingPlan(planPayload)
      }

      this.$alert.success('Lưu kế hoạch giảng dạy thành công')
      this.isEditMode = false

      await this.fetchOne(this.$route.params.id)
    },

    async dowloadExampleFile() {
      try {
        const link = document.createElement('a')
        link.href = '/DanhSachKHGD.xlsx'
        link.setAttribute('download', 'DanhSachKHGD.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        this.$alert.error('Không thể tải file mẫu. Vui lòng thử lại.')
      }
    },
    async uploadExcelFile() {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.xlsx'
      input.click()

      input.onchange = async e => {
        const file = e.target.files[0]
        if (!file) return

        const isExcel = file.name.endsWith('.xlsx')

        if (!isExcel) {
          this.$alert.error('Không đúng định dạng file. Hãy chắc chắn file có đuôi .xlsx')
          return
        }

        const reader = new FileReader()
        reader.onload = event => {
          try {
            const data = new Uint8Array(event.target.result)
            const workbook = XLSX.read(data, { type: 'array' })

            const mainSheet = workbook.Sheets[workbook.SheetNames[0]]
            const refSheet = workbook.Sheets['Ref']

            if (!refSheet) {
              this.$alert.error('Thiếu sheet Ref trong file Excel')
              return
            }

            // Parse Ref sheet
            const refData = XLSX.utils.sheet_to_json(refSheet, { header: 1, defval: '' })
            const activityTypesRef = []
            const locationsRef = []
            const weeksRef = []
            for (const row of refData) {
              const activity = row[0] ? row[0].toString().trim() : ''
              const location = row[1] ? row[1].toString().trim() : ''
              const week = row[2] ? parseInt(row[2]) : null

              if (activity) activityTypesRef.push(activity)
              if (location) locationsRef.push(location)
              if (week && !isNaN(week)) weeksRef.push(`Tuần ${week}`)
            }

            if (activityTypesRef.length === 0 || locationsRef.length === 0 || weeksRef.length === 0) {
              this.$alert.error('Dữ liệu trong sheet Ref không đầy đủ hoặc chưa nhập.')
              return
            }

            // Mapping title -> status từ constant
            const activityTypeMap = this.teachingPlanActivityTypes.reduce((map, item) => {
              map[item.title] = item.status
              return map
            }, {})

            const locationMap = this.teachingPlanLocations.reduce((map, item) => {
              map[item.title] = item.status
              return map
            }, {})

            const jsonData = XLSX.utils.sheet_to_json(mainSheet, { defval: '' })

            let currentTopicName = ''
            let currentLessonName = ''
            const groupedByTopic = []
            const topicMap = {}

            for (let index = 0; index < jsonData.length; index++) {
              const row = jsonData[index]
              const topicRaw = row['Chủ đề']
              const lessonRaw = row['Nội dung']
              const activityTypeTitle = (row['Loại hình'] || '').trim()
              const locationTitle = (row['Địa điểm'] || '').trim()
              const weekRaw = row['Tuần']

              if (topicRaw) currentTopicName = topicRaw.trim()
              if (lessonRaw) currentLessonName = lessonRaw.trim()
              const week = weekRaw ? `Tuần ${parseInt(weekRaw)}` : null

              // Validate dữ liệu
              if (activityTypeTitle && !activityTypesRef.includes(activityTypeTitle)) {
                this.$alert.error(
                  `Dòng ${index + 2}: Loại hình "${activityTypeTitle}" không hợp lệ. Kiểm tra lại sheet Ref.`
                )
                return
              }
              if (locationTitle && !locationsRef.includes(locationTitle)) {
                this.$alert.error(
                  `Dòng ${index + 2}: Địa điểm "${locationTitle}" không hợp lệ. Kiểm tra lại sheet Ref.`
                )
                return
              }
              if (week && !weeksRef.includes(week)) {
                this.$alert.error(`Dòng ${index + 2}: Tuần "${week}" không hợp lệ. Kiểm tra lại sheet Ref.`)
                return
              }

              const activityType = activityTypeMap[activityTypeTitle] || ''
              const location = locationMap[locationTitle] || ''

              // Nhóm chủ đề
              if (!topicMap[currentTopicName]) {
                topicMap[currentTopicName] = {
                  tempId: uuidv4(),
                  name: currentTopicName,
                  lessons: []
                }
                groupedByTopic.push(topicMap[currentTopicName])
              }

              // Gộp tiết giống nhau trong cùng chủ đề (nếu đã tồn tại)
              const existingLesson = topicMap[currentTopicName].lessons.find(l => l.name === currentLessonName)
              if (existingLesson) {
                this.$alert.error(
                  `Dòng ${
                    index + 2
                  }: Nội dung "${currentLessonName}" đã tồn tại trong chủ đề "${currentTopicName}". Vui lòng kiểm tra lại.`
                )
                return
              }

              topicMap[currentTopicName].lessons.push({
                tempId: uuidv4(),
                name: currentLessonName || '',
                activityType: activityType,
                location: location,
                week: week
              })
            }

            this.topics = groupedByTopic
            this.isEditMode = true
            this.$alert.success('Tải dữ liệu thành công từ file Excel')
          } catch (err) {
            console.error(err)
            this.$alert.error('Lỗi khi xử lý file Excel')
          }
        }

        reader.onerror = () => {
          this.$alert.error('Không thể đọc file Excel')
        }

        reader.readAsArrayBuffer(file)
      }
    },

    async exportExcel() {
      try {
        const wb = XLSX.utils.book_new()

        // Sheet 1: Danh sách kế hoạch
        const teachingData = []
        let lessonIndex = 1
        for (const topic of this.topics) {
          for (const lesson of topic.lessons) {
            teachingData.push({
              'Chủ đề': topic.name,
              Tiết: lessonIndex++,
              'Nội dung': lesson.name,
              'Loại hình':
                (this.teachingPlanActivityTypes.find(x => x.status === lesson.activityType) || {}).title || '',
              'Địa điểm': (this.teachingPlanLocations.find(x => x.status === lesson.location) || {}).title || '',
              Tuần: lesson.week ? parseInt(lesson.week.replace('Tuần ', '')) : ''
            })
          }
        }
        const mainSheet = XLSX.utils.json_to_sheet(teachingData, { skipHeader: false })
        XLSX.utils.book_append_sheet(wb, mainSheet, 'Kế hoạch')

        // Sheet 2: Ref
        const refRows = []
        for (let i = 0; i < 42; i++) {
          refRows.push([
            (this.teachingPlanActivityTypes[i] || {}).title || '',
            (this.teachingPlanLocations[i] || {}).title || '',
            i + 1
          ])
        }
        const refSheet = XLSX.utils.aoa_to_sheet(refRows)
        XLSX.utils.book_append_sheet(wb, refSheet, 'Ref')

        const fileName = `DanhSachKHGD_export_${new Date().toISOString().slice(0, 10)}.xlsx`
        XLSX.writeFile(wb, fileName)
      } catch (error) {
        console.error(error)
        this.$alert.error('Đã có lỗi xảy ra khi xuất Excel.')
      }
    }
  }
}
</script>

<style scoped>
.v-btn {
  text-transform: none;
}
.vertical-line-fixed {
  width: 20px;
  border-right: 1px solid;
  transform: translate(10px, 0px);
  flex-shrink: 0;
}

.content-area {
  width: calc(100% - 20px);
  padding-left: 1px;
}
.custom-tooltip {
  background-color: white;
  color: black;
  box-shadow: 0px 2px 12px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  border-radius: 4px;
}
.dotted-line-info {
  position: relative;
  padding-left: 16px;
  margin-left: 7px;
}

.dotted-line-info::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-left: 2px dashed #ccc;
}

.dotted-line-info::after {
  content: '';
  position: absolute;
  top: 32px;
  left: 0;
  width: 32px;
  border-top: 2px dashed #ccc;
}

.dotted-line-info.last-item::before {
  height: 16px;
  bottom: auto;
  top: 16px;
}

.is-edit.dotted-line-info {
  position: relative;
  padding-left: 16px;
  margin-left: 7px;
}

.is-edit.dotted-line-info::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-left: 2px dashed #ccc;
}

.is-edit.dotted-line-info::after {
  content: '';
  position: absolute;
  top: 40px;
  left: 0;
  width: 32px;
  border-top: 2px dashed #ccc;
}

.is-edit.dotted-line-info.last-item::before {
  height: 16px;
  bottom: auto;
  top: 16px;
}

.dotted-line {
  position: relative;
  padding-left: 16px;
  margin-left: 7px;
}

.dotted-line::before {
  content: '';
  position: absolute;
  left: 0;
  top: 6px;
  bottom: 0;
  border-left: 2px dashed #ccc;
}

.dotted-line::after {
  content: '';
  position: absolute;
  top: 38px;
  left: 0;
  width: 32px;
  border-top: 2px dashed #ccc;
}

.dotted-line.last-item::before {
  height: 32px;
  bottom: auto;
}
</style>
